import { AppRoutes } from "AppRoutes";
import classNames from "classnames";
import { environment } from "models/Environment";
import { NavigationModel } from "models/NavigationModel";
import { usePermissions } from "providers/Permissions";
import { useTranslations } from "providers/Translations";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { LayoutContext } from "./LayoutContext";

export const LayoutProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const i18n = useTranslations();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdministrator, isBoardMember, isBoardOwner } = usePermissions();

  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);

  const appClassName = useMemo(
    () =>
      classNames(
        "app",
        `sbb-environment-${environment.shortName}`,
        pathname == AppRoutes.VotingDone || pathname == AppRoutes.VotingOpen ? "has-background" : ""
      ),
    [pathname]
  );

  const handleNavClick = useCallback(
    (href: string) => {
      if (href.includes("://")) {
        window.location.href = href;
      } else {
        navigate(href);
      }
      setIsHamburgerOpen(false);
    },
    [navigate]
  );

  const navItems = useMemo<NavigationModel[]>(() => {
    const items: NavigationModel[] = [
      {
        text: i18n.Navigation_Open_Votes,
        isSelected: pathname.startsWith(AppRoutes.VotingOpen),
        onClick: () => handleNavClick(AppRoutes.VotingOpen),
      },
      {
        text: i18n.Navigation_Completed_Votes,
        isSelected: pathname.startsWith(AppRoutes.VotingDone),
        onClick: () => handleNavClick(AppRoutes.VotingDone),
      },
      {
        text: i18n.Navigation_My_Boards,
        isSelected: pathname.startsWith(AppRoutes.BoardsMy),
        disabled: !isBoardMember && !isBoardOwner,
        onClick: () => handleNavClick(AppRoutes.BoardsMy),
      },
      {
        text: i18n.Navigation_All_Boards,
        isSelected: pathname.startsWith(AppRoutes.BoardsAll),
        disabled: !isAdministrator,
        onClick: () => handleNavClick(AppRoutes.BoardsAll),
      },
    ];

    if (isAdministrator) {
      items.push({
        href: AppRoutes.toAdmin(),
        text: i18n.Navigation_Administration,
        isSelected: false,
      });
    }

    return items;
  }, [i18n, pathname, isAdministrator, isBoardMember, isBoardOwner, handleNavClick]);

  const contextValue = useMemo(
    () => ({
      isFooterVisible,
      setIsFooterVisible,
      isHamburgerOpen,
      setIsHamburgerOpen,
      navItems,
    }),
    [isFooterVisible, isHamburgerOpen, navItems]
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      <div className={appClassName}>{children}</div>
    </LayoutContext.Provider>
  );
};
